import React from 'react';
import { FORM_ERROR } from 'final-form';
import FinalForm, { Form as BaseFinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { ObjectSchema } from 'yup';
import { log } from '@economic/react-app';
import { useToast, ToastReference } from '@economic/taco';
import { convertErrors } from './final-form-utils';
import createCalculations, { Calculation } from './final-form-calculate';
import createValidationHandler from './handlers/createValidationHandler';
import { getAPIErrorValidation, getErrorMessage } from '../../../../utilities/parseAPIValidationErrors';
import { BaseFormWithArrowKeyNavigation } from './BaseFormWithArrowKeyNavigation';

interface Props extends Pick<FinalForm.FormProps, Exclude<keyof FinalForm.FormProps, 'initialValues' | 'onSubmit' | 'validate'>> {
    id?: string;
    className?: string;
    children: ((props: FinalForm.FormRenderProps) => React.ReactNode) | React.ReactNode;
    context?: object;
    initialData?: object;
    horizontal?: boolean;
    messages?: {
        success?: string | React.ReactElement;
        loading?: string;
        error?: string;
    };
    onChange?: any;
    onSubmit: (values: any) => Promise<any> | void;
    onSubmitted?: (response: any) => void;
    onSubmitting?: (values: any, FORM_ERROR: string) => void;
    onSubmitError?: (error: any) => void;
    skipNotifications?: boolean;
    updates?: ((context?: any) => Calculation[]) | Calculation[];
    validate?: (values: object) => string | undefined;
    validationSchema?: ((values: object) => ObjectSchema<{}>) | ObjectSchema<{}>;
    validateOnBlur?: boolean;
    onArrowKeySaveAndChange?: (nextItemId: string | number) => void;
    collectionData?: any[];
    idKeyProp?: string;
    currentItemId?: string | number;
}

const FormFieldContext = React.createContext({});
export const useFormFieldRegistration = () => React.useContext(FormFieldContext);

const Form = (props: Props) => {
    const {
        id,
        className,
        children,
        context,
        initialData = {},
        horizontal = false,
        messages = {},
        mutators,
        onChange,
        onSubmit,
        onSubmitted,
        onSubmitting,
        onSubmitError,
        skipNotifications = false,
        updates,
        validate,
        validationSchema,
        validateOnBlur = true,
        onArrowKeySaveAndChange,
        collectionData,
        idKeyProp,
        currentItemId,
        ...restProps
    } = props;
    const toast = useToast();
    const registeredFields = React.useRef({});
    const calculator = React.useMemo(() => {
        const calculations = typeof updates === 'function' ? updates(context) : updates;
        return calculations ? createCalculations(...calculations) : undefined;
    }, [updates, JSON.stringify(context)]);
    const decorators: any = React.useMemo(() => (calculator ? [calculator] : undefined), [calculator]);

    let toastRef: ToastReference | null = null;
    const handleSubmit = async values => {
        if (!skipNotifications) {
            toastRef = toast.loading(messages.loading ?? '');
        }

        if (onSubmitting) {
            onSubmitting(values, FORM_ERROR);
        }

        try {
            const response = await onSubmit(values);

            if (!skipNotifications && toastRef) {
                toastRef.success(messages.success ?? '');
            }

            if (onSubmitted) {
                onSubmitted(response?.data);
            }
        } catch (error) {
            if (error?.response && error.response.status >= 500) {
                log.error(error as Error);
            }

            error.validation = getAPIErrorValidation(error, values);

            if (!skipNotifications) {
                const message = getErrorMessage(error, messages.error, registeredFields.current);

                toastRef?.error(message);
            }

            if (onSubmitError) {
                onSubmitError(error);
            }

            return convertErrors(error.validation);
        }
    };

    const validator = React.useMemo(
        () => createValidationHandler(validationSchema, validate, context),
        [JSON.stringify(context)]
    );
    const registerField = React.useCallback((name, label) => {
        registeredFields.current[name] = label;
    }, []);

    return (
        <FormFieldContext.Provider value={registerField}>
            <BaseFinalForm
                {...restProps}
                decorators={decorators}
                initialValues={initialData}
                mutators={{ ...mutators, ...arrayMutators }}
                onSubmit={handleSubmit}
                validate={validator}
                validateOnBlur={validateOnBlur}>
                {(renderProps: FinalForm.FormRenderProps) => {
                    return (
                        <BaseFormWithArrowKeyNavigation
                            id={id}
                            collectionData={collectionData}
                            currentItemId={currentItemId}
                            idKeyProp={idKeyProp}
                            handleSubmit={handleSubmit}
                            validator={validator}
                            onArrowKeySaveAndChange={onArrowKeySaveAndChange}
                            children={children}
                            renderProps={renderProps}
                            className={className}
                            horizontal={horizontal}
                            onChange={onChange}
                        />
                    );
                }}
            </BaseFinalForm>
        </FormFieldContext.Provider>
    );
};

Form.ErrorMessage = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} className="form__error" />;

export default Form;
